/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    func, bool, string, object, array,
} from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Slider from 'react-slick';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { useDispatch } from 'react-redux';

import ProductInfoDivider from './ProductInfoDivider';

import { handleVaseClick } from '../ProductPageFunctions';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const useStyles = makeStyles((theme) => ({
    vaseSelectorMainWrapper: {
        padding: '0!important',
        '& .slick-arrow': {
            cursor: 'pointer',
        },
        '& .slick-prev, & .slick-next': {
            '&:before': {
                content: '""',
                fontSize: '0',
            },
        },
        [theme.breakpoints.down(theme.breakpoints.minWidth.small_screen_up)]: {
            '& .slick-next': {
                right: '-6px',
            },
            '& .slick-prev': {
                left: '-6px',
            },
        },
        '& .slick-slide': {
            paddingRight: '0px',
            paddingLeft: '0px',
            marginRight: '-1px',
            height: '100%',
            textAlign: 'center',
            [theme.breakpoints.down('576')]: {
                paddingLeft: '0',
            },
            '&.slick-active': {
                [theme.breakpoints.down('576')]: {
                    paddingLeft: '0',
                },
            },
        },
        '& .text-icon': {
            color: theme.palette.common?.gray_900,
        },
        '& p': {
            padding: 0,
            margin: '0 0 10px',
        },
    },
    vaseSelectorInnerWrapper: {
        [theme.breakpoints.down('md')]: {
            padding: '0px 15px !important',
        },
    },
    varientWrpper: {
        display: 'flex',
        '& button': {
            padding: '6px 4px 4px 4px',
            fontSize: '0.813rem',
            marginRight: 26,
            textTransform: 'uppercase',
            cursor: 'pointer',
            background: 'transparent',
            border: '1px solid #7D7D6C',
            minWidth: '100px',
        },
    },
    activeVase: {
        border: '1px solid #7D7D6C !important',
    },
    activeVariant: {
        background: '#C9C9BA !important',
    },
    vaseTemplate: {
        width: '80px !important',
        height: '80px',
        margin: 'auto',
        cursor: 'pointer',
        background: 'transparent',
        border: 'none',
        display: 'flex!important',
        alignItems: 'center',
        justifyContent: 'center',
        '& img': {
            width: '75px !important',
            height: '75px !important',
        },
        // Media queries for different window sizes
        '@media (max-width: 1058px)': {
            width: '60px !important',
            height: '60px',
            '& img': {
                width: '55px !important',
                height: '55px !important',
            },
        },
        '@media (max-width: 750px)': {
            width: '80px !important',
            height: '80px',
            '& img': {
                width: '75px !important',
                height: '75px !important',
            },
        },
    },
    vaseInnerWrapper: {
        margin: 'auto',
        '& .slick-arrow.slick-disabled': {
            display: 'none',
        },
        [theme.breakpoints.up('sm')]: {
            width: '92%',
        },
    },
    vaseNormalWrapper: {
        display: 'flex',
        gridGap: '10px',
        '& button': {
            width: 80,
            height: 80,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            margin: 'unset',
            '& img': {
                height: 75,
                width: 75,
                cursor: 'pointer',
            },
        },
    },
}));

let sliderSettings = {
    infinite: false,
    speed: 500,
    slidesToShow: 4,
    swipeToSlide: true,
    slidesToScroll: 1,
    touchThreshold: 100,
};

/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-nested-ternary */
const NextArrow = (props) => {
    const { onClick, className } = props;
    return (
        <button type="button" aria-label="Next Arrow" className={`${className}`} onClick={onClick}>
            <ChevronRightIcon className="material-icons text-icon" />
        </button>
    );
};
NextArrow.propTypes = {
    onClick: func,
    className: object.isRequired,
};

NextArrow.defaultProps = {
    onClick: () => {},
};

const PrevArrow = (props) => {
    const { onClick, className } = props;
    return (
        <button type="button" aria-label="Prev Arrow" className={`${className}`} onClick={onClick}>
            <ChevronLeftIcon className="material-icons text-icon" />
        </button>
    );
};

PrevArrow.propTypes = {
    onClick: func,
    className: object.isRequired,
};
PrevArrow.defaultProps = {
    onClick: () => {},
};

const ProductVaseSelector = (props) => {
    const classes = useStyles();
    const {
        getVase, variantList, variantLength, variantVaseList, commonStates, isSmallScreen, vaseList, uniqueVariantSize,
    } = props;

    const variantVaseListForSlider = variantVaseList;

    const itemToShowInSlider = !isSmallScreen ? 6 : 5;
    if (variantLength && variantVaseList.length >= itemToShowInSlider) {
        sliderSettings.slidesToShow = !isSmallScreen ? typeof window === 'undefined' && window && window.innerWidth >= 1024 && window.innerWidth <= 1264 ? 4 : 6 : window && window.innerWidth < 310 ? 2 : 3;
    }

    sliderSettings = { ...sliderSettings, nextArrow: <NextArrow />, prevArrow: <PrevArrow /> };
    const selectedSku = commonStates.selectedSku.id;

    const dispatch = useDispatch();

    const trackVaseSelector = (eventAction, currentVase) => dispatch(trackEvent({
        eventName: 'feature_interaction',
        eventCategory: 'Product Page',
        eventAction,
        eventLabel: `${commonStates.selectedSku?.name} - ${currentVase}`,
    }));
    const renderVariantName = (item) => item.name.replace(/ .*/, '');
    if (!uniqueVariantSize.length) return null;
    const sizeSelector = {
        S: 'Small',
        M: 'Medium',
        L: 'Large',
        D: 'Double',
        T: 'Triple',
    };
    const isActiveVariant = variantVaseList[0]?.id[11];
    return (
        <div className={classes.vaseSelectorMainWrapper}>
            <div className={classes.vaseSelectorInnerWrapper}>
                <p><strong>1.</strong> Select your size.</p>
                <div className={classes.varientWrpper}>
                    {uniqueVariantSize?.map((item) => (
                        <div key={item.price_name}>
                            <button className={`${(item.id[11] === isActiveVariant) ? classes.activeVariant : null}`} type="button" onClick={() => getVase(sizeSelector[item.id[11]], item.id, item.id[11], vaseList, variantList, commonStates, trackVaseSelector, '')}>{sizeSelector[item.id[11]] || renderVariantName(item)}</button>
                        </div>
                    ))}
                </div>
            </div>
            <ProductInfoDivider />
            <div className={classes.vaseSelectorInnerWrapper}>
                <p><strong>2.</strong> Select your container.</p>
                {variantLength && variantVaseList.length >= itemToShowInSlider ? (
                    <div className={classes.vaseInnerWrapper}>
                        <Slider {...sliderSettings}>
                            {variantVaseListForSlider
                            && variantVaseListForSlider.map((item) => (
                                <button type="button" className={`${classes.vaseTemplate} ${selectedSku === item.id ? classes.activeVase : null}`} key={item.id} onClick={() => handleVaseClick(item.id, commonStates, trackVaseSelector, '')} title={item.name}>
                                    {item.vase_image && <img src={item.vase_image} alt={item.code} />}
                                </button>
                            ))}
                        </Slider>
                    </div>
                ) : (
                    <>
                        {variantLength && variantVaseList.length > 0 && (
                            <div className={classes.vaseNormalWrapper}>
                                {variantVaseList.map((item) => (
                                    <button className={`${classes.vaseTemplate} ${selectedSku === item.id ? classes.activeVase : null}`} type="button" key={item.code} onClick={() => handleVaseClick(item.id, commonStates, trackVaseSelector)} title={item.name}>
                                        {item.vase_image && <img src={item.vase_image} alt={item.code} />}
                                    </button>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

ProductVaseSelector.propTypes = {
    variantLength: func.isRequired,
    state: object.isRequired,
    getBrowserName: string.isRequired,
    pdpGAEvents: func.isRequired,
    isFromQuickView: bool.isRequired,
    ENUMS: object.isRequired,
    toggleVase: func.isRequired,
    getVase: func.isRequired,
    variantList: array.isRequired,
    variantVaseList: object.isRequired,
    commonStates: object.isRequired,
    isSmallScreen: bool.isRequired,
    vaseList: array.isRequired,
    uniqueVariantSize: array.isRequired,
};

export default ProductVaseSelector;
